<script setup>
import { ref, computed, watch, watchEffect, provide } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import { Tooltip } from 'flowbite-vue';

import { useGlobalStore } from '@/stores/GlobalStore'
import { useUserStore } from '@/stores/UserStore'
import { useSearchStore } from '@/stores/SearchStore'
import { useBatchStore } from '@/stores/BatchStore'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useCreateBatchStore } from '@/stores/CreateBatchStore'
import { icons } from '@/helpers/icons'
import { replaceMarkdownText } from '@/helpers/text'
import GlobalSearchComponent from './GlobalSearch/index.vue'
import FeedbackDialog from './components/FeedbackDialog.vue';

const globalStore = useGlobalStore()
const userStore = useUserStore()
const searchStore = useSearchStore()
const batchStore = useBatchStore()
const strapiStore = useStrapiStore()
const createBatchStore = useCreateBatchStore()

const router = useRouter()
const route = useRoute()

const user = computed(() => userStore.user)

const config = computed(() => batchStore.config)

const loggedIn = computed(() => userStore.loggedIn)

const sidebar = computed(() => globalStore.sidebar)

const windowWidth = computed(() => globalStore.windowWidth)

const showAdminBanner = computed(() => globalStore.showAdminBanner)
const showSystemNotificationBar = computed(() => Object.keys(strapiStore.notificationStatus).length !== 0)
const systemNotificationBar = computed(() => strapiStore.notificationStatus)

const showingAside = computed(() => {
  let ok = true

  if (
    route.fullPath.includes('export-batch')
    || route.fullPath.includes('create-batch')
    || route.name === 'AddTypes'
    || route.name === 'AddSides'
    || route.name === 'AddUpload'
    || route.name === 'AddCert'
  ) {
    ok = false
  }

  return ok
})

const showFeedbackDialog = ref({
  open: false
})
provide('showFeedbackDialog', showFeedbackDialog)

const dashboardRoutes = ref([
  { route: '/', text: 'Home', icon: 'heroicons:home' },
  { route: '/batches', text: 'Batches', icon: icons.empty_batch },
  { route: '/inventory', text: 'Inventory', icon: 'heroicons:tag' },
  { route: '/connections', text: 'Connections', icon: 'tabler:layout-grid-add' },
])

const secondaryRoutes = ref([
  // { route: '/refer-friend', text: 'Refer a friend', icon: 'heroicons:gift' },
  { text: 'Feedback', icon: 'heroicons:megaphone' },
  { route: '/changelog', text: 'News', icon: 'heroicons:newspaper' },
  { link: 'https://support.carddealerpro.com/hc/en-us', text: 'Help & Docs', icon: 'heroicons:question-mark-circle' },
])

const scans = ref({
  current: 0,
  full: 0,
})

const topPlanName = 'Hall of Fame'

const closeTabletSidebar = () => {
  if (windowWidth.value >= 768 && windowWidth.value < 1280) {
    if (globalStore.sidebar) {
      globalStore.sidebar = false
      localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
    }
  }
}

const toggleSidebar = () => {
  globalStore.sidebar = !globalStore.sidebar
  localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
}

const closeSidebar = () => {
  if (windowWidth.value < 1280) {
    globalStore.sidebar = false
    localStorage.setItem('sidebar', JSON.stringify(globalStore.sidebar))
  }
}

const signOut = () => {
  userStore.logOut()
  closeSidebar()
}

const cdpBatchesSearch = () => {
  if (batchStore.selectedBatch?.type) {
    batchStore.batchKind = batchStore.selectedBatch.type
    searchStore.searchUserBatches({ kind: batchStore.selectedBatch.type }) 
  } else {
    searchStore.searchUserBatches() 
  }

  searchStore.updateSearchStateBasedOnDataObject(
    {
      cdp_search: {
        ...searchStore.cdp_search,
        open: true,
        state: 'batches',
        locationOfSearchCall: 'add-button'
      },
      searchState: 'result',
      selectedFilter: 'Batches'
    }
  )
}

const isBatchDetailsFLow = computed(() => {
  let ok = false
  if (
    route.matched &&
    route.matched.length > 1 &&
    route.matched[1].name === 'BatchDetailsFlow'
  ) {
    ok = true
  }

  return ok
})

const addToCurrentBatch = () => {
  if (isBatchDetailsFLow.value) {
    router.push(`/batches/${route.params.batchId}/add/types`)
  }
}

const handleUpgrade = () => {
  globalStore.openPlansModal = true
  router.push('/settings/plans')
}

const reloadPage = () => {
  window.location.reload();
}

const handleOpenSearch = () => {
  searchStore.triggerOpenSearchResult = true
}

const handleCreateNewBatch = () => {
  batchStore.selectedBatchId = null
  createBatchStore.clearCreatingBatchInfo()
  router.push('/create-batch/general-settings')
}

watch(config, () => {
  if (batchStore.config) {
    scans.value = {
      current: batchStore.config.totalScans - batchStore.config.availableScans,
      full: batchStore.config.totalScans
    }
  }
})

watchEffect(() => {
  if (
    route.name === 'Homepage'
    || route.name === 'Batches'
    || route.name === 'Inventory'
    || route.name === 'Connections'
  ) {
    if (!localStorage?.sidebar) {
      globalStore.sidebar = true
    }
  } else {
    if (!localStorage?.sidebar) {
      globalStore.sidebar = false
    }
  }
})
</script>

<template>
  <Transition name="fade">
    <div v-if="showSystemNotificationBar" class="fixed w-full top-0 left-0 h-10 px-3 z-[1001] flex justify-between bg-gradient-to-r from-[#111928] to-[#F05252]">
      <div class="flex items-center gap-2.5 w-1/6">
        <span v-html="icons.notification_bell" />
        <span class="text-[#F05252] text-xs font-bold">{{systemNotificationBar.headline}}</span>
      </div>

      <div class="flex items-center justify-center flex-grow text-center w-2/3">
        <p class="text-white text-[13px] max-w-[800px] font-normal break-words"
        v-html="replaceMarkdownText(systemNotificationBar.message, '#FFF')">
        </p>
      </div>
      <div class="flex items-center w-1/6">
        <button class="text-white text-xs px-4 py-2 ml-auto" @click="reloadPage()">Reload</button>
      </div>
    </div>
  </Transition>
  
  <Transition name="fade">
    <section
      class="h-10 flex items-center justify-between px-3 text-xs bg-[#FED025] fixed top-0 left-0 w-full z-50"
      v-if="showAdminBanner"
    >
      <h4 class="uppercase">Admin preview</h4>
      <div class="flex items-center gap-x-3">
        <span>Logged in as {{userStore.user?.email}}</span>
        <router-link to="/sign-in" class="font-semibold text-tint-1 transition hover:text-tint-2" @click="signOut()">
          Log Out
        </router-link>
      </div>
      <button @click="globalStore.hideAdminBanner = true">
        <Icon icon="heroicons:x-mark" class="text-lg text-gray-600" />
      </button>
    </section>
  </Transition>
  <section
    class="h-full flex pt-12 transition-all duration-300"
    :class="{ 'xl:!pl-72': sidebar, '!pt-[88px]': showAdminBanner || showSystemNotificationBar, 'sm:pl-[60px]': showingAside, 'sm:!pl-72': sidebar }"
  >
    <section class="main-layout" v-click-outside="closeTabletSidebar">
      <header
        class="fixed left-0 w-full z-[1000] h-12 bg-tint-1"
        :class="showAdminBanner || showSystemNotificationBar ? 'top-10': 'top-0'"
      >
        <div class="flex items-center justify-between h-full py-1.5 px-4 md:px-[18px]">
          <div class="flex items-center gap-x-6">
            <button
              @click="toggleSidebar"
              class="w-6 h-6 flex-shrink-0 fill-white"
            >
              <span v-html="icons.nav_open" v-if="sidebar" />
              <span v-html="icons.nav_collapsed" v-else />
            </button>

            <!-- FRONTEND TASKS: Globalize MenuOptions -->
            <Menu as="div" class="relative" v-slot="{ open, close }" v-if="userStore.plan">
              <MenuButton
                class="
                  flex items-center gap-x-1.5 py-1.5 pl-3 pr-4 flex-grow border border-blue-600 rounded-full bg-gray-800/10
                  text-white
                "
                @click="closeSidebar"
                :disabled="!batchStore.config?.hasActiveSubscription"
              >
                <span v-html="icons.card_add" />
                <h4 class="text-sm font-semibold whitespace-nowrap">Add Cards</h4>
              </MenuButton>
              <Transition name="menu-scale">
                <MenuItems
                  class="
                    z-10 bg-white rounded-md shadow-sm border w-max absolute top-full left-0 translate-y-1 text-sm
                    py-2 flex flex-col
                  "
                  as="ul"
                >
                  <MenuItem
                    as="button"
                    class="flex items-center w-full text-left gap-x-3 px-4 py-2 transition duration-300 hover:bg-gray-100 order-1"
                    v-if="isBatchDetailsFLow"
                    @click="addToCurrentBatch"
                  >
                    <span v-html="icons.existing_batch" />
                    <span class="flex-grow">Add to <b>this</b> Batch</span>
                  </MenuItem>
                  <MenuItem as="button" class="order-3">
                    <span @click="[handleCreateNewBatch(), close()]" class="flex items-center w-full text-left gap-x-3 px-4 py-2 transition duration-300 hover:bg-gray-100">
                      <span v-html="icons.blank_batch" />
                      <span class="flex-grow">Create a new batch</span>
                    </span> 
                  </MenuItem>
                  <MenuItem
                    as="button"
                    class="flex items-center w-full text-left gap-x-3 px-4 py-2 transition duration-300 hover:bg-gray-100 order-4"
                    @click="cdpBatchesSearch"
                  >
                    <span v-html="icons.existing_batch" />
                    <span class="flex-grow">{{ batchStore.selectedBatch ? 'Add to an existing batch' : 'Add to a batch' }}</span>
                  </MenuItem>
              </MenuItems>
              </Transition>
            </Menu>
          </div>

          <GlobalSearchComponent v-if="userStore.plan" />

          <div class="flex items-center gap-x-4">
            <button @click="handleUpgrade" v-if="(batchStore.config?.subscription?.plan_id !== batchStore.topPlan?.id) || !batchStore.config?.hasActiveSubscription"
              class="
                hidden md:flex items-center gap-x-1.5 pl-1.5 pr-2.5 py-2 transition duration-300 bg-blue-50 text-tint-1 rounded
              "
              :class="{'!p-0': !batchStore.config?.subscription?.plan_id}"
            >
              <template v-if="batchStore.config?.subscription?.plan_id && (!batchStore.config?.subscription || (batchStore.config?.hasActiveSubscription && batchStore.config?.subscription?.plan_id !== batchStore.topPlan?.id))">
                <Icon icon="heroicons:fire-16-solid" />
                <span class="text-xs font-semibold">Upgrade</span>
                <!-- <span class="inline-block text-xs pl-2 border-l border-gray-300">5 days left</span> -->
              </template>
              <template v-else-if="(batchStore.config?.subscription?.plan_id && batchStore.config?.subscription && !batchStore.config?.hasActiveSubscription)">
                <Icon icon="heroicons:bolt" />
                <span class="text-xs font-semibold">Reactivate Account</span>
              </template>
              <template v-else>
                <div class="w-[88px] invisible h-0"></div>
              </template>
            </button>
            <button class="md:hidden text-white" @click="handleOpenSearch">
              <Icon icon="heroicons:magnifying-glass-20-solid" class="text-2xl" />
            </button>
            <!-- FRONTEND TASKS: Globalize MenuOptions -->
            <Menu v-if="loggedIn" as="div" class="relative inline-block text-left" v-slot="{ open, close }">
              <MenuButton class="flex items-center gap-x-0.5" @click="closeSidebar">
                <img
                  v-if="user.avatar_image"
                  :src="user.avatar_image"
                  class="w-8 h-8 object-cover rounded-full ring-1 ring-blue-600"
                />
                <span
                  v-else
                  class="w-8 h-8 object-cover rounded-full ring-1 ring-blue-600 flex items-center justify-center"
                  style="color: #0086FF; background-color: #EBF5FF;"
                  v-text="user.first_name?.slice(0, 1).toUpperCase()"
                />
                <Icon
                  icon="heroicons:chevron-down-16-solid"
                  class="text-2xl text-white transition-all duration-300"
                  :class="{ 'rotate-180': open }"
                />
              </MenuButton>
              <Transition name="menu-scale">
                <MenuItems
                  class="
                    z-10 bg-white divide-y divide-gray-100 rounded-md shadow-sm border w-[14rem] absolute top-full right-0
                    translate-y-0.5 text-sm text-gray-500
                  "
                  as="ul"
                >
                  <li class="px-4 py-3">
                    <h4 class="font-semibold text-gray-900" v-html="`${user.first_name} ${user.last_name}`"></h4>
                    <span class="font-medium truncate" v-html="user.email"></span>
                  </li>
                  <div class="py-1">
                    <MenuItem as="li">
                      <router-link
                        @click="[closeSidebar(), close()]"
                        to="/settings/account"
                        class="block px-4 py-2 transition duration-300 hover:bg-gray-100"
                      >
                        Account Settings
                      </router-link>
                    </MenuItem>
                    <MenuItem as="li">
                      <router-link
                        @click="[closeSidebar(), close()]"
                        to="/settings/plans"
                        class="block px-4 py-2 transition duration-300 hover:bg-gray-100"
                      >
                        Billing
                      </router-link>
                    </MenuItem>
                    <MenuItem as="li">
                      <a href="https://www.carddealerpro.com/trading-card-scanner" target="_blank"
                        @click="[closeSidebar(), close()]"
                        to="/"
                        class="block px-4 py-2 transition duration-300 hover:bg-gray-100"
                      >
                        Get the Desktop App
                      </a>
                    </MenuItem>
                  </div>
                  <MenuItem as="li" class="py-1" v-if="batchStore.config?.hasActiveSubscription && batchStore.config?.subscription?.plan_id !== batchStore.topPlan?.id">
                    <div
                      @click="[closeSidebar(), close()], handleUpgrade()"
                      class="flex  gap-x-1.5 px-4 py-2 transition duration-300 hover:bg-gray-100 cursor-pointer"
                    >
                      <Icon icon="heroicons:fire-16-solid" class="text-base text-tint-1" />
                      <span class="flex-grow">Upgrade</span>
                      <!-- <Icon icon="heroicons:chevron-right-16-solid" class="text-base text-gray-400" /> -->
                  </div>
                  </MenuItem>
                  <MenuItem as="li" class="py-1" v-if="batchStore.config?.subscription && !batchStore.config?.hasActiveSubscription">
                    <div
                      @click="[closeSidebar(), close()], handleUpgrade()"
                      class="flex  gap-x-1.5 px-4 py-2 transition duration-300 hover:bg-gray-100 cursor-pointer"
                    >
                      <Icon icon="heroicons:bolt-16-solid" class="text-base text-tint-1" />
                      <span class="flex-grow">Reactivate Account</span>
                      <!-- <Icon icon="heroicons:chevron-right-16-solid" class="text-base text-gray-400" /> -->
                  </div>
                  </MenuItem>
                  <MenuItem as="li" class="py-2">
                    <router-link
                      to="/sign-in"
                      @click="[signOut(), close()]"
                      class="block w-full text-left px-4 py-2 text-red-600 transition duration-300 hover:bg-red-50"
                    >
                      Log out
                    </router-link>
                  </MenuItem>
              </MenuItems>
              </Transition>
            </Menu>
          </div>
        </div>
      </header>
      <aside
        class="
          flex flex-col gap-y-6 justify-between h-[calc(100%-3rem)] md:border-r pt-4 pb-6 w-[60px] transition-all
          duration-300 fixed -left-[60px] top-12 bg-gray-800 z-[999]
        "
        :class="{
          'md:-left-0': showingAside,
          '!w-full md:!w-72 overflow-hidden !left-0': sidebar,
          '!top-[88px] !h-[calc(100%-88px)]': showAdminBanner || showSystemNotificationBar
        }"
      >
        <section class="flex flex-col gap-y-6">
          <ul class="flex flex-col gap-y-4">
            <li v-for="item, index in dashboardRoutes" :key="index">
              <router-link :to="item.route" v-slot="{ isActive }" @click="closeSidebar" v-if="sidebar">
                <div
                  class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5"
                  :class="{ '!bg-gray-100 hover:!bg-gray-100': isActive }"
                >
                  <span
                    v-if="item.text === 'Batches'"
                    v-html="item.icon"
                    class="inline-block child:w-6 child:h-6 transition duration-300 flex-none"
                    :class="isActive ? 'child:stroke-tint-1' : 'child:stroke-gray-400'"
                  />
                  <Icon
                    :icon="item.icon"
                    class="transition duration-300 text-2xl flex-none"
                    :class="isActive ? 'text-tint-1' : 'text-gray-400'"
                    v-else
                  />
                  <span
                    class="text-sm text-gray-400 transition-all duration-200"
                    :class="{ 'font-semibold text-gray-900': isActive }"
                    v-text="item.text"
                  />
                </div>
              </router-link>
              <Tooltip placement="right" theme="dark" class="w-full child:w-full" v-else>
                <template #trigger>
                  <router-link :to="item.route" v-slot="{ isActive }" @click="closeSidebar">
                    <div
                      class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5"
                      :class="{ '!bg-gray-100 hover:!bg-gray-100': isActive }"
                    >
                      <span
                        v-if="item.text === 'Batches'"
                        v-html="item.icon"
                        class="inline-block child:w-6 child:h-6 transition duration-300 flex-none"
                        :class="isActive ? 'child:stroke-tint-1' : 'child:stroke-gray-400'"
                      />
                      <Icon
                        :icon="item.icon"
                        class="transition duration-300 text-2xl flex-none"
                        :class="isActive ? 'text-tint-1' : 'text-gray-400'"
                        v-else
                      />
                    </div>
                  </router-link>
                </template>
                <template #content>
                  <div class="base-tooltip-2">
                    {{ item.text }}
                  </div>
                </template>
              </Tooltip>
            </li>
          </ul>
        </section>
        <section class="flex flex-col">
          <ul class="flex flex-col gap-y-4 mb-6">
            <li v-for="item, index in secondaryRoutes" :key="index">
              <template v-if="sidebar">
                <router-link :to="item.route" v-slot="{ isActive }" @click="closeSidebar" v-if="item.route">
                  <div
                    class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5"
                    :class="{ '!bg-gray-100 hover:!bg-gray-100': isActive }"
                  >
                    <Icon
                      :icon="item.icon"
                      class="transition duration-300 text-2xl flex-none"
                      :class="isActive ? 'text-tint-1' : 'text-gray-400'"
                    />
                    <span
                      class="text-sm text-gray-400 transition-all duration-200"
                      :class="{ 'font-semibold text-gray-900': isActive }"
                      v-text="item.text"
                    />
                  </div>
                </router-link>
                <template v-else-if="item.text === 'Feedback'">
                  <button class="w-full" @click="showFeedbackDialog = { ...showFeedbackDialog, open: true }">
                    <div
                      class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5"
                      :class="{ '!bg-gray-100 hover:!bg-gray-100': showFeedbackDialog.open }"
                    >
                      <Icon
                        :icon="item.icon"
                        class="transition duration-300 text-2xl flex-none"
                        :class="showFeedbackDialog.open ? 'text-tint-1' : 'text-gray-400'"
                      />
                      <span
                        class="text-sm text-gray-400 transition-all duration-200"
                        :class="{ 'font-semibold text-gray-900': showFeedbackDialog.open }"
                        v-text="item.text"
                      />
                    </div>
                  </button>
                </template>
                <a :href="item.link" @click="closeSidebar" target="_blank" v-else>
                  <div class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5">
                    <Icon :icon="item.icon" class="transition duration-300 text-2xl flex-none text-gray-400" />
                    <span class="text-sm text-gray-400 transition-all duration-200" v-text="item.text" />
                  </div>
                </a>
              </template>
              <template v-else>
                <Tooltip placement="right" theme="dark" class="w-full child:w-full">
                  <template #trigger>
                    <router-link :to="item.route" v-slot="{ isActive }" @click="closeSidebar" v-if="item.route">
                      <div
                        class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5"
                        :class="{ '!bg-gray-100 hover:!bg-gray-100': isActive }"
                      >
                        <Icon
                          :icon="item.icon"
                          class="transition duration-300 text-2xl flex-none"
                          :class="isActive ? 'text-tint-1' : 'text-gray-400'"
                        />
                      </div>
                    </router-link>
                    <template v-else-if="item.text === 'Feedback'">
                      <button class="w-full" @click="showFeedbackDialog = { ...showFeedbackDialog, open: true }">
                        <div
                          class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5"
                          :class="{ '!bg-gray-100 hover:!bg-gray-100': showFeedbackDialog.open }"
                        >
                          <Icon
                            :icon="item.icon"
                            class="transition duration-300 text-2xl flex-none"
                            :class="showFeedbackDialog.open ? 'text-tint-1' : 'text-gray-400'"
                          />
                         
                        </div>
                      </button>
                    </template>
                    <a :href="item.link" @click="closeSidebar" target="_blank" v-else>
                      <div class="p-2 rounded-lg hover:bg-gray-700 transition duration-300 flex items-center gap-x-3 mx-2.5">
                        <Icon :icon="item.icon" class="transition duration-300 text-2xl flex-none text-gray-400" />
                      </div>
                    </a>
                  </template>
                  <template #content>
                    <div class="base-tooltip-2">
                      {{ item.text }}
                    </div>
                  </template>
                </Tooltip>
              </template>
            </li>
          </ul>
    
          <div
            class="
              px-4 shadow-sm border border-gray-600 bg-gray-700 rounded-lg flex flex-col justify-center gap-y-3 text-white
              transition-all duration-200 h-0 overflow-hidden invisible mx-2.5
            "
            :class="{ 'h-16 !visible': sidebar }"
          >
            <header v-if="!batchStore.loadingConfig" class="flex items-center justify-between">
              <div class="text-xs font-medium whitespace-nowrap" :class="{ 'text-red-600': batchStore.config?.availableScans <= 0 }">
                {{ scans.current?.toLocaleString() }} / {{ scans.full?.toLocaleString() }} Scans
              </div>
              <button
                class="tinted-button flex-none cursor-pointer"
                v-if="batchStore.config?.hasActiveSubscription && batchStore.config?.subscription?.plan_id !== batchStore.topPlan?.id"
                @click="handleUpgrade"
              >
                <Icon icon="heroicons:fire-16-solid" class="text-base" />
                <h5 class="whitespace-nowrap" >Upgrade</h5>
              </button>
              <button
                class="tinted-button flex-none cursor-pointer"
                v-if="batchStore.config?.subscription && !batchStore.config?.hasActiveSubscription"
                @click="handleUpgrade"
              >
                <Icon icon="heroicons:bolt-16-solid" class="text-base" />
                <h5 class="whitespace-nowrap" >Select Plan</h5>
              </button>
            </header>
            <div class="w-full bg-gray-800 rounded-full h-1">
              <div
                v-if="!batchStore.loadingConfig"
                class="h-1 rounded-full"
                :class="batchStore.config?.availableScans <= 0 ? 'bg-red-600' : 'bg-tint-1'"
                :style="{ width: scans.current * 100 / scans.full + '%' }"
              />
            </div>
          </div>

          <div class="relative h-8 flex justify-center mt-6">
            <Transition name="fade-fast" mode="out-in">
              <img src="@/assets/images/cdp-logo-small.svg" v-if="sidebar" />
              <img src="@/assets/images/cdp-icon.svg" v-else />
            </Transition>
          </div>
        </section>
      </aside>
    </section>
    <section class="flex flex-col h-full flex-grow">
      <RouterView />
    </section>
  </section>

  <FeedbackDialog />
</template>